import type { ReactElement } from 'react'
import type {
  CaseQuotaStatus,
  ClientProfile,
  CreateCaseInput,
  CreateClientProfileInput,
} from '../../types/case-management'
import { useTheme } from '../../contexts/theme-context'
import {
  CaseNameField,
  ClientPicker,
  CriticalWarning,
  ModalActions,
  ModalHeader,
  OrgReferenceField,
  PriorityPicker,
  QuotaIndicator,
  QuotaWarning,
  ScopeField,
} from './new-case-modal.parts'
import { useNewCaseForm, useNewCaseHandlers, type NewCaseFormState } from './use-new-case-form'

interface NewCaseModalProps {
  isOpen: boolean
  clients: ClientProfile[]
  quotaStatus?: CaseQuotaStatus | null
  isCreating?: boolean
  isCreatingClient?: boolean
  onSubmit?: (data: CreateCaseInput) => void
  onCreateClient?: (data: CreateClientProfileInput) => Promise<ClientProfile | null>
  onClose?: () => void
}

function filterClients(clients: ClientProfile[], search: string): ClientProfile[] {
  const term = search.toLowerCase()
  return clients.filter(
    (client) =>
      client.name.toLowerCase().includes(term) || client.shortName?.toLowerCase().includes(term)
  )
}

interface HandlerArgs {
  onSubmit?: (data: CreateCaseInput) => void
  onCreateClient?: (data: CreateClientProfileInput) => Promise<ClientProfile | null>
}

function buildHandlerArgs(props: NewCaseModalProps): HandlerArgs {
  const args: HandlerArgs = {}
  if (props.onSubmit) args.onSubmit = props.onSubmit
  if (props.onCreateClient) args.onCreateClient = props.onCreateClient
  return args
}

function QuotaSection({
  quotaStatus,
  canCreateCase,
}: {
  quotaStatus: CaseQuotaStatus | null | undefined
  canCreateCase: boolean
}): ReactElement | null {
  if (!quotaStatus) return null
  if (!canCreateCase) return <QuotaWarning quotaStatus={quotaStatus} />
  return <QuotaIndicator quotaStatus={quotaStatus} />
}

interface CaseClientPickerProps {
  form: NewCaseFormState
  clients: ClientProfile[]
  canCreateCase: boolean
  isCreatingClient: boolean
  hasCreateClient: boolean
  onCreateClient: () => void
}

function CaseClientPicker({
  form,
  clients,
  canCreateCase,
  isCreatingClient,
  hasCreateClient,
  onCreateClient,
}: CaseClientPickerProps): ReactElement {
  const selectedClient = clients.find((client) => client.id === form.selectedClientId)
  const filteredClients = filterClients(clients, form.clientSearch)
  return (
    <ClientPicker
      selectedClient={selectedClient}
      showClientDropdown={form.showClientDropdown}
      toggleDropdown={() => {
        form.setShowClientDropdown(!form.showClientDropdown)
      }}
      disabled={!canCreateCase}
      panelProps={{
        filteredClients,
        clientSearch: form.clientSearch,
        setClientSearch: form.setClientSearch,
        showNewClientForm: form.showNewClientForm,
        newClientFormProps: {
          newClientName: form.newClientName,
          setNewClientName: form.setNewClientName,
          newClientShortName: form.newClientShortName,
          setNewClientShortName: form.setNewClientShortName,
          onCancel: () => {
            form.setShowNewClientForm(false)
            form.setNewClientName('')
            form.setNewClientShortName('')
          },
          onCreate: onCreateClient,
          isCreatingClient,
        },
        hasCreateClient,
        onShowNewClientForm: () => {
          form.setShowNewClientForm(true)
        },
        onSelectNoClient: () => {
          form.setSelectedClientId(undefined)
          form.setShowClientDropdown(false)
        },
        onSelectClient: (id) => {
          form.setSelectedClientId(id)
          form.setShowClientDropdown(false)
          form.setClientSearch('')
        },
      }}
    />
  )
}

interface CaseFormProps {
  form: NewCaseFormState
  clients: ClientProfile[]
  canCreateCase: boolean
  isCreating: boolean
  isCreatingClient: boolean
  hasCreateClient: boolean
  onSubmit: (e: React.FormEvent) => void
  onCreateClient: () => void
  onClose?: () => void
}

function CaseForm({
  form,
  clients,
  canCreateCase,
  isCreating,
  isCreatingClient,
  hasCreateClient,
  onSubmit,
  onCreateClient,
  onClose,
}: CaseFormProps): ReactElement {
  return (
    <form onSubmit={onSubmit} className="space-y-5 p-6">
      <CaseNameField value={form.name} onChange={form.setName} disabled={!canCreateCase} />
      <CaseClientPicker
        form={form}
        clients={clients}
        canCreateCase={canCreateCase}
        isCreatingClient={isCreatingClient}
        hasCreateClient={hasCreateClient}
        onCreateClient={onCreateClient}
      />
      <OrgReferenceField
        value={form.orgCaseReference}
        onChange={form.setOrgCaseReference}
        disabled={!canCreateCase}
      />
      <ScopeField
        value={form.scopeDescription}
        onChange={form.setScopeDescription}
        disabled={!canCreateCase}
      />
      <PriorityPicker value={form.priority} onChange={form.setPriority} disabled={!canCreateCase} />
      {form.priority === 'critical' && <CriticalWarning />}
      <ModalActions
        onClose={onClose}
        isValid={form.isValid}
        canCreateCase={canCreateCase}
        isCreating={isCreating}
      />
    </form>
  )
}

export function NewCaseModal(props: NewCaseModalProps): ReactElement | null {
  const { theme } = useTheme()
  const form = useNewCaseForm()
  const { handleSubmit, handleCreateClient } = useNewCaseHandlers(form, buildHandlerArgs(props))

  if (!props.isOpen) return null

  const canCreateCase = props.quotaStatus?.canCreateCase ?? true

  return (
    <div
      className="dashboard fixed inset-0 z-50 flex items-center justify-center p-4"
      data-theme={theme}
    >
      <div
        className="absolute inset-0 backdrop-blur-sm"
        style={{ background: 'rgba(28, 31, 46, 0.6)' }}
        onClick={props.onClose}
      />
      <div
        className="relative max-h-[90vh] w-full max-w-lg overflow-hidden overflow-y-auto rounded-2xl border shadow-2xl"
        style={{
          background: 'var(--bg-card, #ffffff)',
          borderColor: 'var(--border, rgba(28,31,46,0.09))',
        }}
      >
        <ModalHeader onClose={props.onClose} />
        <QuotaSection quotaStatus={props.quotaStatus} canCreateCase={canCreateCase} />
        <CaseForm
          form={form}
          clients={props.clients}
          canCreateCase={canCreateCase}
          isCreating={props.isCreating ?? false}
          isCreatingClient={props.isCreatingClient ?? false}
          hasCreateClient={Boolean(props.onCreateClient)}
          onSubmit={handleSubmit}
          onCreateClient={() => {
            void handleCreateClient()
          }}
          {...(props.onClose ? { onClose: props.onClose } : {})}
        />
      </div>
    </div>
  )
}
